import type {
  LogCategory,
  CreateCategoryDto,
  UpdateCategoryDto,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const CategorySchema: z.ZodType<LogCategory> = z.object({
  id: z.number(),
  workspaceId: z.number(),
  name: z.string(),
  sortOrder: z.number(),
  color: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Response Schemas
const CreateCategoryResponseSchema: z.ZodType<
  DataAndMessageResponse<LogCategory>
> = z.object({
  data: CategorySchema,
  message: z.string(),
});

const GetCategoriesResponseSchema: z.ZodType<
  DataAndMessageResponse<LogCategory[]>
> = z.object({
  data: z.array(CategorySchema),
  message: z.string(),
});

const UpdateCategoryResponseSchema: z.ZodType<
  DataAndMessageResponse<LogCategory>
> = z.object({
  data: CategorySchema,
  message: z.string(),
});

const DeleteCategoryResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

export function createCategoryModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateCategoryDto) => {
      const response = await httpClient.post<
        typeof CreateCategoryResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/categories`, {
        data,
        schema: CreateCategoryResponseSchema,
      });
      return response.data;
    },

    list: async (workspaceId: number) => {
      const response = await httpClient.get<typeof GetCategoriesResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/categories`,
        {
          schema: GetCategoriesResponseSchema,
        },
      );
      return response.data;
    },

    update: async (
      workspaceId: number,
      categoryId: number,
      data: UpdateCategoryDto,
    ) => {
      const response = await httpClient.patch<
        typeof UpdateCategoryResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/categories/${categoryId}`, {
        data,
        schema: UpdateCategoryResponseSchema,
      });
      return response.data;
    },

    remove: async (workspaceId: number, categoryId: number) => {
      const response = await httpClient.delete<
        typeof DeleteCategoryResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/categories/${categoryId}`, {
        schema: DeleteCategoryResponseSchema,
      });
      return response;
    },
  };
}
